import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBusinessInfo,
  updateBusinessInfo,
  resetBusinessState,
} from "../../redux/Slices/businessInfoSlice";
import { fetchCountries, fetchStates } from "../../redux/Slices/locationSlice";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import FormSelect from "../../components/Common/FormSelect";
import pdf from "../../styles/Assets/pdf.svg";
import { Table, Space, Tooltip } from "antd";
import RichTextEditorModal from "../../components/Common/RichTextEditorModal";
import Loader from "../../components/Common/Loader";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import ToastNotification from "../../components/Common/ToastNotification";
import {
  isValidNumberInput,
  formatDate,
  isValidTextInput,
} from "../../utils/Utils";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";

const BusinessInfo = () => {
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();

  const dispatch = useDispatch();
  const { profileData } = useSelector((state) => state.profile);

  const {
    businessInfo,
    isLoading: isBusinessLoading,
    error: businessInfoError,
    updateSuccess,
    updateError,
  } = useSelector((state) => state.business);
  const {
    countries,
    states,
    isLoading: isLocationLoading,
  } = useSelector((state) => state.location);

  const [formData, setFormData] = useState({
    businessId: "",
    businessLegalName: "",
    country: "",
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    businessEntity: "company",
    coaching: {
      locale: "",
      lastPublished: null,
      coachingInfo: "",
      coachingPresentVariables: "",
    },
  });
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [contractInfo, setContractInfo] = useState({ contractLink: "" });
  const [isCoachingInfoEditable, setIsCoachingInfoEditable] = useState(true);
  const [privacyPolicyData, setPrivacyPolicyData] = useState({
    locale: "",
    lastPublished: null,
    pdfURL: "",
  });
  const [toastConfig, setToastConfig] = useState({
    type: "",
    message: "",
    heading: "",
  });
  const [showToast, setShowToast] = useState(false);

  const coachId = profileData?.coachId;

  // Fetch business info and location data on mount
  useEffect(() => {
    if (coachId) {
      dispatch(fetchBusinessInfo({ coachId }));
      dispatch(fetchCountries());
    }
  }, [dispatch, coachId]);

  const formattedDate = (date) => {
    const dateOfBirth = new Date(date);

    // Check if the date is valid (i.e., not "Invalid Date")
    if (isNaN(dateOfBirth.getTime())) {
      return "";
    }

    // Format the valid date as YYYY-MM-DD
    return new Date(
      Date.UTC(
        dateOfBirth.getUTCFullYear(),
        dateOfBirth.getUTCMonth(),
        dateOfBirth.getUTCDate()
      )
    )
      .toISOString()
      .split("T")[0];
  };

  useEffect(() => {
    if (businessInfo) {
      setFormData({
        coachId: coachId,
        businessId: businessInfo.businessId,
        businessLegalName: businessInfo.businessLegalName,
        country: businessInfo.country,
        businessAddress: businessInfo.businessAddress,
        city: businessInfo.city,
        state: businessInfo.state,
        zipCode: businessInfo.zipCode,
        businessEntity: businessInfo.businessEntity,
        coaching: {
          locale: businessInfo?.coaching?.locale,
          // lastPublished: businessInfo?.coaching?.lastPublished || null,
          lastPublished: businessInfo?.coaching?.lastPublished
            ? formattedDate(businessInfo?.coaching?.lastPublished)
            : null,
          coachingInfo: businessInfo?.coaching?.coachingInfo,
          coachingPresentVariables:
            businessInfo?.coaching?.coachingPresentVariables,
        },
      });
      setContractInfo(businessInfo.contractInfo);
      setPrivacyPolicyData(businessInfo.privacyPolicy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessInfo]);

  useEffect(() => {
    if (formData.country) {
      dispatch(fetchStates(formData.country));
    }
    return () => {
      dispatch(resetBusinessState());
    };
  }, [dispatch, formData.country]);

  const countriesArr = countries.map((country) => ({
    value: country.countryId,
    label: country.countryName,
    countryId: country.countryId,
  }));

  const statesArr = states.map((state) => ({
    value: state.stateId,
    label: state.stateName,
    stateId: state.stateId,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "zipCode") {
      if (!isValidNumberInput(value)) {
        return;
      }
    }

    if (name === "city") {
      if (!isValidTextInput(value)) {
        return;
      }
    }

    if (name === "country") {
      const selectedCountry = countriesArr.find(
        (country) => country.countryId === value
      );
      setFormData((prevData) => ({
        ...prevData,
        country: selectedCountry ? selectedCountry.countryId : value,
      }));
      setFormData((prevData) => ({
        ...prevData,
        state: "",
      }));
    } else if (name === "state") {
      setFormData((prevData) => ({
        ...prevData,
        state: value,
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.businessLegalName.trim()) {
      newErrors.businessLegalName =
        trans("Common.business") +
        " " +
        trans("Common.name") +
        " " +
        trans("Common.isRequired");
    } else if (formData.businessLegalName.trim().length < 3) {
      newErrors.businessLegalName =
        trans("Common.business") +
        " " +
        trans("Common.name") +
        " " +
        trans("Common.isRequiredAndMinLength");
    }
    if (!formData.country)
      newErrors.country =
        trans("Common.country") + " " + trans("Common.isRequired");

    if (!formData.businessAddress.trim()) {
      newErrors.businessAddress =
        trans("Common.business") +
        " " +
        trans("Common.address") +
        " " +
        trans("Common.isRequired");
    } else if (formData.businessAddress.trim().length < 3) {
      newErrors.businessAddress =
        trans("Common.business") +
        " " +
        trans("Common.address") +
        " " +
        trans("Common.isRequiredAndMinLength");
    }

    if (!formData.city.trim()) {
      newErrors.city = trans("Common.city") + " " + trans("Common.isRequired");
    } else if (formData.city.trim().length < 3) {
      newErrors.businessLegalName =
        trans("Common.city") +
        " " +
        trans("Common.name") +
        " " +
        trans("Common.isRequiredAndMinLength");
    }

    if (!formData.state)
      newErrors.state =
        trans("Common.state") + " " + trans("Common.isRequired");

    if (!formData.zipCode)
      newErrors.zipCode =
        trans("Common.zipCode") + " " + trans("Common.isRequired");

    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      if (!formData.coaching.lastPublished) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        formData.coaching.lastPublished = `${yyyy}-${mm}-${dd}`;
      }
      dispatch(updateBusinessInfo(formData));
    }
  };

  // Table columns for coaching and Privacy Policy
  const columns = [
    {
      title: trans("Common.locale"),
      dataIndex: "locale",
      key: "locale",
      width: "45%",
      render: () =>
        formData.coaching.locale === "en-US"
          ? "English-US"
          : formData.coaching.locale,
    },
    {
      title: trans("Common.last") + " " + trans("Common.published"),
      dataIndex: "lastPublished",
      key: "lastPublished",
      width: "45%",
      render: () =>
        formData.coaching.lastPublished
          ? formatDate(formData.coaching.lastPublished)
          : "-",
    },
    {
      title: trans("Common.action"),
      key: "action",
      width: "10%",
      render: () => (
        <Space size="small">
          <Tooltip title={trans("Common.edit")}>
            <Button
              type="button"
              onClick={() => handleEdit(formData.coaching.coachingInfo)}
              className="action-buttons pl-0"
            >
              <i className="bi bi-pencil"></i>
            </Button>
          </Tooltip>
          <Tooltip title={trans("Common.view")}>
            <Button
              type="button"
              onClick={() => handleView()}
              className="action-buttons pl-0"
            >
              <i className="bi bi-eye"></i>
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const columns1 = [
    {
      title: trans("Common.locale"),
      dataIndex: "locale",
      key: "locale",
      width: "45%",
      render: () =>
        privacyPolicyData.locale === "en-US"
          ? "English-US"
          : privacyPolicyData.locale,
    },
    {
      title: trans("Common.last") + " " + trans("Common.published"),
      dataIndex: "lastPublished",
      key: "lastPublished",
      width: "45%",
      render: () => formatDate(privacyPolicyData.lastPublished),
    },
    {
      title: trans("Common.action"),
      key: "action",
      width: "10%",
      render: () => (
        <Space size="small" className="ml-2">
          <Tooltip title={trans("Common.view")}>
            <a
              href={privacyPolicyData.pdfURL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
            >
              <img src={pdf} alt="pdf" />
            </a>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleEdit = (record) => {
    setFormData((prevState) => ({
      ...prevState,
      coaching: {
        ...prevState.coaching,
        coachingInfo: record,
      },
    }));
    setIsCoachingInfoEditable(true);
    setShowModal(true);
  };

  const handleView = () => {
    setIsCoachingInfoEditable(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSavePublish = (activeVariables) => {
    setFormData((prevState) => {
      const updatedFormData = {
        ...prevState,
        coaching: {
          ...prevState.coaching,
          coachingPresentVariables: { ...activeVariables },
        },
      };
      dispatch(updateBusinessInfo(updatedFormData));
      return updatedFormData;
    });
    setShowModal(false);
  };

  const handleEditorChange = (value) => {
    setFormData((prevState) => ({
      ...prevState,
      coaching: {
        ...prevState.coaching,
        coachingInfo: value,
      },
    }));
  };

  // Toast notification logic
  useEffect(() => {
    if (updateSuccess) {
      setToastConfig({
        type: "success",
        message: trans("Toolbox.businessInfoUpdatedSuccessfully"),
        heading: trans("Common.success"),
      });
      setShowToast(true);
    } else if (updateError) {
      setToastConfig({
        type: "error",
        message: trans("Toolbox.businessInfoUpdatedFailed"),
        heading: trans("Common.failed"),
      });
      setShowToast(true);
    } else if (businessInfoError) {
      setToastConfig({
        type: "error",
        message: businessInfoError,
        heading: trans("Common.failed"),
      });
      setShowToast(true);
    }
  }, [updateSuccess, updateError, businessInfoError, trans]);

  return (
    <div className=" formContainer pt-0 mb-6">
      {isBusinessLoading ? (
        !businessInfoError ? (
          <Loader />
        ) : (
          <div className="my-5 d-flex flex-column justify-content-center align-items-center">
            <p className="mt-3 fs-5">{businessInfoError}</p>
          </div>
        )
      ) : (
        <Form
          className="templateWrapper px-4 py-4 m-0 p-0"
          onSubmit={handleSubmit}
        >
          <Row className="mb-4">
            <Row>
              <Col>
                <h5 className="pb-2">
                  {trans("Common.business") +
                    " " +
                    trans("Common.registration")}
                </h5>
              </Col>
              <Col className="d-flex justify-content-end">
                <Button
                  className="saveButton"
                  type="submit"
                  disabled={isBusinessLoading}
                >
                  <i className="bi bi-floppy2-fill"></i> {trans("Common.save")}
                </Button>
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} lg={3} className="mt-3">
                <Form.Group controlId="formBusinessLegalName">
                  <FormLabel
                    label={
                      trans("Common.business") +
                      " " +
                      trans("Toolbox.legalName")
                    }
                    required
                  />
                  <FormControl
                    type="text"
                    placeholder={
                      trans("Common.enter") +
                      " " +
                      trans("Common.business") +
                      " " +
                      trans("Toolbox.legalName")
                    }
                    name="businessLegalName"
                    value={formData.businessLegalName}
                    onChange={handleChange}
                    isInvalid={!!errors.businessLegalName}
                    feedback={errors.businessLegalName}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={3} className="mt-3">
                <FormSelect
                  label={trans("Common.country")}
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  options={[
                    { value: "", label: trans("Common.selectCountry") },
                    ...countriesArr,
                  ]}
                  isInvalid={!!errors.country}
                  feedback={errors.country}
                  labelRequired
                />
              </Col>
              <Col xs={12} md={6} lg={3} className="mt-3">
                <FormSelect
                  label={trans("Common.state")}
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  isInvalid={!!errors.state}
                  feedback={errors.state}
                  labelRequired
                  loading={isLocationLoading}
                  options={[{ value: "", label: "Select State" }, ...statesArr]}
                />
              </Col>
              <Col xs={12} md={6} lg={3} className="mt-3">
                <Form.Group controlId="formCity">
                  <FormLabel label={trans("Common.city")} required />
                  <FormControl
                    type="text"
                    placeholder={
                      trans("Common.enter") + " " + trans("Common.city")
                    }
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    isInvalid={!!errors.city}
                    feedback={errors.city}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={3} className="mt-3">
                <Form.Group controlId="formBusinessAddress">
                  <FormLabel
                    label={
                      trans("Common.business") + " " + trans("Common.address")
                    }
                    required
                  />
                  <FormControl
                    type="text"
                    placeholder={
                      trans("Common.enter") +
                      " " +
                      trans("Common.business") +
                      " " +
                      trans("Common.address")
                    }
                    name="businessAddress"
                    value={formData.businessAddress}
                    onChange={handleChange}
                    isInvalid={!!errors.businessAddress}
                    feedback={errors.businessAddress}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={3} className="mt-3">
                <Form.Group controlId="formZipCode">
                  <FormLabel label={trans("Common.zipCode")} required />
                  <FormControl
                    type="string"
                    placeholder={
                      trans("Common.enter") + " " + trans("Common.zipCode")
                    }
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    isInvalid={!!errors.zipCode}
                    feedback={errors.zipCode}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={12} lg={6} className="d-flex flex-column mt-3">
                <Form.Group>
                  <FormLabel
                    label={
                      trans("Common.business") + " " + trans("Common.entity")
                    }
                    required
                  />
                  <div className="d-flex flex-column">
                    <Row>
                      <Col>
                        <Form.Check
                          type="radio"
                          label={trans("Common.company")}
                          name="businessEntity"
                          value="company"
                          checked={formData.businessEntity === "company"}
                          onChange={handleChange}
                          isInvalid={!!errors.businessEntity}
                        />
                        <Form.Text className="text-muted ml-4">
                          {trans("Toolbox.registeredBusinessEntity")}
                        </Form.Text>
                      </Col>
                      <Col>
                        <Form.Check
                          type="radio"
                          label={trans("Toolbox.soloProprietorship")}
                          name="businessEntity"
                          value="Sole"
                          checked={formData.businessEntity === "Sole"}
                          onChange={handleChange}
                          isInvalid={!!errors.businessEntity}
                        />
                        <Form.Text className="text-muted ml-4">
                          {trans("Toolbox.companyOneEmployee")}
                        </Form.Text>
                      </Col>
                    </Row>
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {errors.businessEntity}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Row>
          <hr className="p-1" />
          <Row>
            <h5>
              {trans("Common.contract") + " " + trans("Common.information")}
            </h5>
            <Row>
              <p>
                <img src={pdf} alt="pdf" className="d-inline mr-1" />
                {contractInfo?.contractLink ? (
                  <a
                    href={contractInfo?.contractLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                  >
                    {contractInfo?.contractDetails?.fileName
                      ? contractInfo?.contractDetails?.fileName
                      : ""}
                  </a>
                ) : (
                  <span>{trans("Common.noContractLink")}</span>
                )}
              </p>
            </Row>
          </Row>
          <hr className="p-1" />
          <Row className="pb-3">
            <h5>
              {trans("Common.coaching") + " " + trans("Common.information")}
            </h5>
            {formData?.coaching ? (
              <Row>
                <Table
                  className="custom-table"
                  columns={columns}
                  dataSource={[formData?.coaching]}
                  pagination={false}
                  loading={isBusinessLoading}
                  rowKey="locale"
                  tableLayout="fixed"
                  components={{
                    table: (props) => (
                      <table
                        {...props}
                        style={{
                          ...props.style,
                          borderCollapse: "separate",
                          borderSpacing: "0 10px",
                        }}
                      />
                    ),
                    header: {
                      cell: (props) => {
                        return (
                          <th
                            {...props}
                            style={{
                              ...props.style,
                              background: "transparent",
                              fontFamily: "Proxima-Nova-Regular",
                              borderColor: "#FCFBFFCC",
                              borderStyle: "solid",
                              borderWidth: "8px 0px",
                              padding: "12px 16px",
                              // Border radius for header cells
                              borderRadius:
                                props.children === columns[0].title
                                  ? "12px 0 0 12px"
                                  : props.children ===
                                    columns[columns.length - 1].title
                                  ? "0 12px 12px 0"
                                  : "0",
                            }}
                          />
                        );
                      },
                      row: (props) => (
                        <tr
                          {...props}
                          style={{
                            ...props.style,
                            background: "transparent",
                            fontFamily: "Proxima-Nova-Regular",
                          }}
                        />
                      ),
                    },
                    body: {
                      cell: (props) => (
                        <td
                          {...props}
                          style={{
                            ...props.style,
                            background: "transparent",
                            borderColor: "transparent",
                            fontFamily: "Proxima-Nova-Regular",
                            borderStyle: "solid",
                            borderWidth: "8px 0px",
                            padding: "12px 16px",
                            // Border radius for body cells
                            borderRadius:
                              props.index % columns.length === 0
                                ? "12px 0 0 12px"
                                : props.index % columns.length ===
                                  columns.length - 1
                                ? "0 12px 12px 0"
                                : "0",
                          }}
                        />
                      ),
                      row: (props) => (
                        <tr
                          {...props}
                          style={{
                            ...props.style,
                            background: "transparent",
                            fontFamily: "Proxima-Nova-Regular",
                            margin: "10px 0",
                          }}
                        />
                      ),
                    },
                  }}
                />
              </Row>
            ) : (
              <p>{trans("Common.noCoachingInfo")}</p>
            )}
          </Row>
          <hr className="p-1" />
          <Row>
            <h5>{trans("Common.privacyPolicy")}</h5>
            {privacyPolicyData?.locale ? (
              <Row>
                <Table
                  className="custom-table"
                  columns={columns1}
                  dataSource={[privacyPolicyData]}
                  pagination={false}
                  loading={isBusinessLoading}
                  rowKey="locale"
                  tableLayout="fixed"
                  components={{
                    table: (props) => (
                      <table
                        {...props}
                        style={{
                          ...props.style,
                          borderCollapse: "separate",
                          borderSpacing: "0 10px",
                        }}
                      />
                    ),
                    header: {
                      cell: (props) => {
                        return (
                          <th
                            {...props}
                            style={{
                              ...props.style,
                              background: "transparent",
                              borderColor: "#FCFBFFCC",
                              fontFamily: "Proxima-Nova-Regular",
                              borderStyle: "solid",
                              borderWidth: "8px 0px",
                              padding: "12px 16px",
                              // Border radius for header cells
                              borderRadius:
                                props.children === columns1[0].title
                                  ? "12px 0 0 12px"
                                  : props.children ===
                                    columns1[columns1.length - 1].title
                                  ? "0 12px 12px 0"
                                  : "0",
                            }}
                          />
                        );
                      },
                      row: (props) => (
                        <tr
                          {...props}
                          style={{
                            ...props.style,
                            fontFamily: "Proxima-Nova-Regular",
                            background: "transparent",
                          }}
                        />
                      ),
                    },
                    body: {
                      cell: (props) => (
                        <td
                          {...props}
                          style={{
                            ...props.style,
                            background: "transparent",
                            borderColor: "transparent",
                            fontFamily: "Proxima-Nova-Regular",
                            borderStyle: "solid",
                            borderWidth: "8px 0px",
                            padding: "12px 16px",
                            // Border radius for body cells
                            borderRadius:
                              props.index % columns1.length === 0
                                ? "12px 0 0 12px"
                                : props.index % columns1.length ===
                                  columns1.length - 1
                                ? "0 12px 12px 0"
                                : "0",
                          }}
                        />
                      ),
                      row: (props) => (
                        <tr
                          {...props}
                          style={{
                            ...props.style,
                            fontFamily: "Proxima-Nova-Regular",
                            background: "transparent",
                            margin: "10px 0",
                          }}
                        />
                      ),
                    },
                  }}
                />
              </Row>
            ) : (
              <p>{trans("Common.noPrivacyPolicyInfo")}</p>
            )}
          </Row>
          <RichTextEditorModal
            showModal={showModal}
            handleCloseModal={handleCloseModal}
            handleEditorChange={handleEditorChange}
            editorContent={formData?.coaching?.coachingInfo}
            handleSavePublish={handleSavePublish}
            coachingPresentVariables={
              formData?.coaching?.coachingPresentVariables
            }
            isCoachingEditable={isCoachingInfoEditable}
            formData={formData}
            profileData={profileData}
            privacyPolicyData={privacyPolicyData}
            countriesArr={countriesArr}
            statesArr={statesArr}
          />
        </Form>
      )}
      {showToast && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default BusinessInfo;
